// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import { fmtDateTime } from '../utils/timezoneUtils';
import { useStargateHealth } from '../context/StargateHealthContext';
import { useTheme } from '../context/ThemeContext';
import StargateSystemInfo from './StargateSystemInfo';
import StargateContainers from './StargateContainers';
import StargateSystemLogs from './StargateSystemLogs';
import StargateModemStatus from './StargateModemStatus';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Grid from '@mui/material/Grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiOffIcon from '@mui/icons-material/WifiOff';

const StargateHealthAwareWidgets = ({ ip, apiKey, stargate }) => {
  const { healthy, checking, error, refreshHealth, hasCheckedOnce, initialized } =
    useStargateHealth();
  const { isDarkMode } = useTheme();

  // ABSOLUTELY NO WIDGETS until health check completes AND passes
  // This is the most important check - if we haven't checked once, show loading
  if (!initialized || !hasCheckedOnce || checking) {
    return (
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <CircularProgress size={18} thickness={5} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Checking Stargate connectivity...
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={140}
              sx={{ borderRadius: '8px' }}
              animation="wave"
            />
          ))}
        </Box>
      </Box>
    );
  }

  // If health check completed but healthy is not explicitly true, don't render widgets
  if (healthy !== true) {
    // Show offline state for any non-healthy state
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
        <Card
          sx={{
            background: isDarkMode ? 'background.paper' : '#ffffff',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            boxShadow: isDarkMode ? 2 : '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <WifiOffIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2, color: 'error.main' }}>
              Unable to Connect to Stargate
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              {error && error.message
                ? error.message
                : 'The connection to the Stargate could not be established.'}
            </Typography>
            {error && error.type === 'authentication' && (
              <Alert severity="warning" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                <strong>Tip:</strong> Check that the API key is correctly configured for this
                Stargate.
              </Alert>
            )}
            {error && error.type === 'network' && (
              <Alert severity="info" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                <strong>Tip:</strong> Verify the Stargate's IP address and ensure it's connected to
                the network.
              </Alert>
            )}
            <Button
              variant="contained"
              startIcon={checking ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={refreshHealth}
              disabled={checking}
            >
              {checking ? 'Checking...' : 'Retry Connection'}
            </Button>
          </CardContent>
        </Card>

        {/* Show minimal info even when offline */}
        <Card
          sx={{
            background: isDarkMode ? 'background.paper' : '#ffffff',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            boxShadow: isDarkMode ? 2 : '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Device Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Model
                </Typography>
                <Typography variant="body1">{stargate.model}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Serial Number
                </Typography>
                <Typography variant="body1">{stargate.serial_number}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  IP Address
                </Typography>
                <Typography variant="body1">{stargate.ip_address || 'Not set'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Last Seen (MQTT)
                </Typography>
                <Typography variant="body1">
                  {stargate.last_seen ? fmtDateTime(stargate.last_seen) : 'Never'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Only render widgets if we're absolutely sure the stargate is healthy
  // Render all widgets when healthy
  const cardStyle = {
    background: isDarkMode ? 'background.paper' : '#ffffff',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 3,
    boxShadow: isDarkMode ? '0 1px 3px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
        maxWidth: 900,
        mx: 'auto',
      }}
    >
      <Card sx={cardStyle}>
        <CardContent sx={{ position: 'relative', minHeight: 180 }}>
          <StargateSystemInfo ip={ip} apiKey={apiKey} showLoadingOverlay />
        </CardContent>
      </Card>
      <Card sx={cardStyle}>
        <CardContent sx={{ position: 'relative', minHeight: 180 }}>
          <StargateModemStatus ip={ip} apiKey={apiKey} showLoadingOverlay />
        </CardContent>
      </Card>
      <Card sx={cardStyle}>
        <CardContent sx={{ position: 'relative', minHeight: 180 }}>
          <StargateContainers ip={ip} apiKey={apiKey} showLoadingOverlay />
        </CardContent>
      </Card>
      <Card sx={cardStyle}>
        <CardContent sx={{ position: 'relative', minHeight: 180 }}>
          <StargateSystemLogs ip={ip} apiKey={apiKey} showLoadingOverlay />
        </CardContent>
      </Card>
      <Card sx={cardStyle}>
        <CardContent sx={{ position: 'relative', minHeight: 180, p: 0 }}>
          <React.Suspense fallback={null}>
            {ip &&
              apiKey &&
              React.createElement(require('./StargateVpnClientWidgets').default, {
                ip: ip,
                apiKey: apiKey,
              })}
          </React.Suspense>
        </CardContent>
      </Card>
      <Card sx={cardStyle}>
        <CardContent sx={{ position: 'relative', minHeight: 180, p: 0 }}>
          <React.Suspense fallback={null}>
            {ip &&
              apiKey &&
              React.createElement(require('./StargateHostWireguardConfig').default, {
                ip: ip,
                apiKey: apiKey,
              })}
          </React.Suspense>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StargateHealthAwareWidgets;
